import React from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import ErrorBoundary from 'app/SearchPage/components/ErrorBoundary'
import { Header } from 'app/SearchPage/components/Header'
import QuickFilters from 'app/SearchPage/components/QuickFilters'
import SearchBox from 'app/SearchPage/components/SearchBox'
import SearchFilterSheet from 'app/SearchPage/components/SearchFilterSheet'
import SearchResultList from 'app/SearchPage/components/SearchResultList'
import { ERROR_STATE_TRACKER_ACTIVITY_TYPE } from 'app/SearchPage/constants'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import { getErrorType } from 'app/SearchPage/utils/error'
import { useContentSearch, useContentSearchFilter } from 'stores/content-search'

const SearchPage = () => {
  const router = useRouter()
  const formMethods = useForm({
    mode: 'onChange',
  })
  const {
    formState: { errors },
    setFocus,
  } = formMethods
  const { hasSearchQueryFromURL, searchQueryFromURL } = useSearchQuery()
  const contentSearchFilterStore = useContentSearchFilter()
  const contentSearchStore = useContentSearch()
  const queryClient = useQueryClient()
  const { SETrackSearchResultsPage } = useSETrackEvent()

  const resetBoundaryHandler = (error) => {
    contentSearchStore.setErrorType('')

    if (['INTERNAL_SERVER_ERROR', 'NETWORK_ERROR'].includes(error)) {
      queryClient.refetchQueries({
        queryKey: [
          'content-search-results-infinite-query',
          searchQueryFromURL,
          {
            formats: contentSearchFilterStore.selectedFormats,
            topics: contentSearchFilterStore.selectedTopics,
          },
        ],
      })
    } else if (error === 'NOT_FOUND_ERROR') {
      setFocus('searchQuery')
    } else if (error === 'NOT_FOUND_WITH_FILTERS_ERROR') {
      contentSearchFilterStore.setSheetOpen(true)
    }
  }

  const setQuickFilterState = () => {
    const isAnyFilterSelected = contentSearchFilterStore.hasAnyFilterSelected()
    contentSearchFilterStore.setShowQuickFilters(isAnyFilterSelected)
  }

  const trackErrorBoundary = (errorType: string) => {
    if (!ERROR_STATE_TRACKER_ACTIVITY_TYPE?.[errorType]) return

    const activityType = ERROR_STATE_TRACKER_ACTIVITY_TYPE[errorType]
    SETrackSearchResultsPage(activityType)
  }

  const errorBoundaryHandler = (error) => {
    const incomingErrorType = getErrorType(error)

    if (contentSearchStore.errorType === incomingErrorType) return

    setQuickFilterState()
    trackErrorBoundary(incomingErrorType)
    contentSearchStore.setErrorType(incomingErrorType as string)
  }

  React.useEffect(() => {
    const incomingFilterFormats = Array.isArray(router.query?.formats ?? [])
      ? router.query?.formats ?? []
      : [router.query?.formats]
    const incomingFilterTopics = Array.isArray(router.query?.topics ?? [])
      ? router.query?.topics ?? []
      : [router.query?.topics]
    contentSearchFilterStore.setSelectedFilters(
      incomingFilterFormats as string[],
      incomingFilterTopics as string[]
    )
    contentSearchStore.setErrorType('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query])

  React.useEffect(() => {
    if (hasSearchQueryFromURL) {
      SETrackSearchResultsPage('LOAD_SEARCH_RESULTS_PAGE', {
        event_trigger: 'SEARCH_PAGE',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearchQueryFromURL])

  return (
    <div className="relative max-w-xl m-auto">
      <FormProvider {...formMethods}>
        <div className="fixed z-50 w-full max-w-xl bg-white">
          <Header />
        </div>

        <div className="fixed top-0 bottom-0 z-40 w-full max-w-xl px-4 pb-3 bg-white pt-14 h-max">
          <SearchBox />
        </div>

        {hasSearchQueryFromURL &&
        contentSearchFilterStore.isShowQuickFilters ? (
          <QuickFilters
            className={clsx(
              contentSearchFilterStore.isShowQuickFilters && 'pt-28',
              !contentSearchFilterStore.isShowQuickFilters && 'pt-0',
              Boolean(errors?.searchQuery?.message) && '!mt-6'
            )}
          />
        ) : null}

        {hasSearchQueryFromURL && (
          <SearchFilterSheet
            isOpen={contentSearchFilterStore.isSheetOpen}
            onClose={() => contentSearchFilterStore.setSheetOpen(false)}
          />
        )}

        <div
          className={clsx(
            contentSearchFilterStore.isShowQuickFilters && 'pt-0',
            !contentSearchFilterStore.isShowQuickFilters && 'pt-28'
          )}
        >
          <ErrorBoundary
            resetKeys={[
              router.asPath,
              contentSearchFilterStore.selectedFormats.join(','),
              contentSearchFilterStore.selectedTopics.join(','),
              contentSearchStore.errorType,
            ]}
            onReset={resetBoundaryHandler}
            onError={errorBoundaryHandler}
          >
            {hasSearchQueryFromURL && <SearchResultList />}
          </ErrorBoundary>
        </div>
      </FormProvider>
    </div>
  )
}

export default SearchPage
